import React, { useState, useRef, useEffect } from 'react';
import { 
  MapPinIcon, 
  ChevronDownIcon, 
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  BuildingLibraryIcon,
  HomeIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';

/**
 * LocationSwitcher Component
 * 
 * A dropdown component that allows users to switch between their assigned locations.
 * Displays current location and provides a list of available locations to switch to.
 * 
 * Features:
 * - Shows current location with icon
 * - Dropdown list of all available locations
 * - Visual indicator for current selection
 * - Location type icons
 * - Responsive design
 * - Click outside to close
 * - Persists last used location in localStorage (survives browser restart)
 * - Automatically restores last used location on login
 */
const LocationSwitcher = () => {
  const {
    currentLocation,
    availableLocations: allLocations,
    isLoading,
    switchLocation,
    hasMultipleLocations
  } = useLocation();

  // Filter out expired locations - only show active locations
  const availableLocations = allLocations.filter(userLocation => 
    userLocation.status === 'active'
  );

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle location change
  const handleLocationChange = (locationId) => {
    if (currentLocation?._id === locationId || currentLocation?.id === locationId) {
      setIsOpen(false);
      return;
    }
    
    switchLocation(locationId);
    setIsOpen(false);
  };

  // Get icon based on location type
  const getLocationIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'headquarters':
      case 'regional_office':
      case 'office':
        return BuildingLibraryIcon;
      case 'warehouse':
      case 'distribution_center':
      case 'cold_storage':
        return BuildingOfficeIcon;
      case 'retail_store':
      case 'branch':
      case 'service_center':
        return BuildingStorefrontIcon;
      default:
        return MapPinIcon;
    }
  };

  // Get location type label
  const getLocationTypeLabel = (type) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // If loading, show skeleton
  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-2 py-1.5 bg-gray-100 rounded-lg animate-pulse h-9">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="w-24 h-3 bg-gray-300 rounded"></div>
      </div>
    );
  }

  // Check if current location is expired
  const currentUserLocation = allLocations.find(ul => 
    (ul.locationId._id || ul.locationId.id) === (currentLocation?._id || currentLocation?.id)
  );
  const isCurrentLocationExpired = currentUserLocation && currentUserLocation.status !== 'active';

  // If no locations, show message
  if (!currentLocation) {
    return (
      <div className="flex items-center space-x-2 px-2 py-1.5 bg-gray-100 rounded-lg text-gray-500 text-xs h-9">
        <MapPinIcon className="w-4 h-4" />
        <span>No location assigned</span>
      </div>
    );
  }

  // If current location is expired, show warning
  if (isCurrentLocationExpired) {
    const LocationIcon = getLocationIcon(currentLocation.type);
    
    return (
      <div className="flex items-center space-x-2 px-2 py-1 bg-red-50 border border-red-200 rounded-lg h-9">
        <LocationIcon className="w-4 h-4 text-red-600 flex-shrink-0" />
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-xs font-medium text-red-700 truncate leading-tight">
            {currentLocation.name} (Expired)
          </span>
          <span className="text-[10px] text-red-500 truncate leading-tight">
            Access expired - Please switch location
          </span>
        </div>
      </div>
    );
  }

  // If only one active location, show without dropdown
  if (availableLocations.length <= 1) {
    const LocationIcon = getLocationIcon(currentLocation.type);
    
    return (
      <div className="flex items-center space-x-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg h-9">
        <LocationIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-xs font-medium text-gray-900 truncate leading-tight">
            {currentLocation.name}
          </span>
          {currentLocation.type && (
            <span className="text-[10px] text-gray-500 truncate leading-tight">
              {getLocationTypeLabel(currentLocation.type)}
            </span>
          )}
        </div>
      </div>
    );
  }

  const CurrentLocationIcon = getLocationIcon(currentLocation.type);

  return (
    <div className="relative z-50" ref={dropdownRef}>
      {/* Current Location Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-2 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-9"
      >
        <CurrentLocationIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="flex flex-col items-start justify-center min-w-0">
          <span className="text-xs font-medium text-gray-900 truncate max-w-[120px] leading-tight">
            {currentLocation.name}
          </span>
          {currentLocation.type && (
            <span className="text-[10px] text-gray-500 truncate max-w-[120px] leading-tight">
              {getLocationTypeLabel(currentLocation.type)}
            </span>
          )}
        </div>
        <ChevronDownIcon 
          className={`w-3 h-3 text-gray-500 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Available Locations ({availableLocations.length})
            </div>
            
            {availableLocations.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No active locations available
              </div>
            ) : (
              availableLocations.map((userLocation) => {
              const location = userLocation.locationId;
              const locationId = location._id || location.id;
              const isCurrentLocation = 
                locationId === currentLocation._id || 
                locationId === currentLocation.id;
              const LocationIcon = getLocationIcon(location.type);

              return (
                <button
                  key={locationId}
                  onClick={() => handleLocationChange(locationId)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                    isCurrentLocation
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <LocationIcon 
                    className={`w-5 h-5 flex-shrink-0 ${
                      isCurrentLocation ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  />
                  
                  <div className="flex-1 flex flex-col items-start min-w-0">
                    <span className={`text-sm font-medium truncate w-full ${
                      isCurrentLocation ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {location.name}
                    </span>
                    
                    <div className="flex items-center space-x-2 w-full">
                      {location.type && (
                        <span className={`text-xs truncate ${
                          isCurrentLocation ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                          {getLocationTypeLabel(location.type)}
                        </span>
                      )}
                      
                      {location.address?.city && (
                        <span className={`text-xs truncate ${
                          isCurrentLocation ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                          • {location.address.city}
                        </span>
                      )}
                    </div>

                    {userLocation.role && (
                      <span className={`text-xs px-2 py-0.5 rounded-full mt-1 ${
                        userLocation.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : userLocation.role === 'manager'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {userLocation.role}
                      </span>
                    )}
                  </div>

                  {isCurrentLocation && (
                    <CheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </button>
              );
            })
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-3 bg-gray-50 rounded-b-lg">
            <div className="text-xs text-gray-500 text-center">
              Switching location will refresh the page
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;
